// Block D (2026-10-08): the CRM pipeline on the v2 kit, after design/business/mockups/crm-pipeline.html.
//
// Data: GET /api/crm-boards (boards, a card count each, and whether this person may change the
// pipeline: owner and admin only, decisions 23, 28, 39); GET /api/crm-boards/:id/contacts (the
// stages with their cards, and cards with no stage, shown in their own "Unassigned" lane rather
// than silently merged into the first stage); GET /api/crm-boards/:id (the board's columns);
// GET /api/follow-up/nudges (who needs a follow-up, decisions 41 to 45: not a rule of our own).
//
// Honest states: every load failure is an ErrorState with Try again, never an empty board; every
// failed write says the server's own words and puts things back. Moves offer Undo; removing a
// card from a board, deleting a board and deleting an old interaction wait out the Undo window
// (decision 7) before anything is sent.
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { api } from '../../api/client';
import { useContacts } from '../../contexts/ContactsContext';
import { useInvoices } from '../../contexts/InvoicesContext';
import { useClientHealth } from '../../hooks/useClientHealth';
import useAutoTour from '../../tours/useAutoTour';
import useChatIntro from '../../tours/useChatIntro';
import { showToast, showErrorToast, showSuccessToast } from '../../utils/toast';
import { messageFromError } from '../../utils/apiError';
import { currencySymbol } from '../../utils/currency';
import {
  PageLayout, PageHeader, Button, Chip, SegmentedControl, SearchInput, StatTile, MoveToSheet,
  EmptyState, ErrorState, Skeleton, Banner, useDelayedDelete, v, HeaderButton,
} from '../../ui';
import { KanbanColumn, PipelineList } from './CrmBoard';
import CrmDrawer from './CrmDrawer';
import { ContactFormModal, CreateBoardModal, BoardSettingsModal, BoardSetup } from './CrmModals';
import {
  lanesFrom, pipelineStats, moveCard as moveIn, stageOfCard, stageColor, boardColor, boardCurrency, money,
  isSignedLongAgo, isWonStage, isLostStage, UNASSIGNED_ID,
} from './crmPipeline';
import './crm.css';

const VIEW_KEY = 'aidey_crm_view';
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'board'; } catch { return 'board'; } };
const saveView = (view) => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* per-viewer convenience only */ } };

// boardId: which board these lanes are. Item 18 (2026-10-20): for the frame between a board being
// chosen and its GET starting, the screen drew the new board from the state left by the old one
// (or by "no board": no stages, not loading), so every board flashed the set-up card and asked for
// the templates. Lanes are now shown only for the board they were loaded for; anything else is
// loading.
const EMPTY_BOARD = { boardId: null, loading: true, error: null, lanes: [], columns: [] };

export default function CRMDashboard() {
  useAutoTour('crm');
  useChatIntro('crm');

  const { contacts, setContacts } = useContacts();
  const { invoices } = useInvoices();
  const [tasks, setTasks] = useState([]);
  const { scores, recalculate } = useClientHealth({ contacts, tasks, invoices });

  // Boards
  const [boardsState, setBoardsState] = useState({ loading: true, error: null });
  const [boards, setBoards] = useState([]);
  const [managePipeline, setManagePipeline] = useState(false);
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [loadedBoard, setBoard] = useState(EMPTY_BOARD);
  const board = loadedBoard.boardId === activeBoardId ? loadedBoard
    : { ...EMPTY_BOARD, boardId: activeBoardId, loading: !!activeBoardId };
  const lanesRef = useRef([]);
  lanesRef.current = board.lanes;
  const loadSeq = useRef(0);

  // Follow-ups: the shared engine (decisions 41-45), one group per contact.
  const [allFollowUps, setFollowUps] = useState({});
  const [followState, setFollowState] = useState({ loading: true, error: null });

  // UI
  const [view, setViewState] = useState(readView);
  const setView = (next) => { setViewState(next); saveView(next); };
  const [search, setSearch] = useState('');
  const [followOnly, setFollowOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const dragRef = useRef(null);
  const [moveFor, setMoveFor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── Loads ─────────────────────────────────────────────────────────────────
  const loadFollowUps = useCallback(async () => {
    setFollowState({ loading: true, error: null });
    try {
      const res = await api('/api/follow-up/nudges');
      const d = await res.json();
      const map = {};
      for (const g of d.groups || []) map[g.contactId] = g;
      // An older server sent nudges only; keep the first per contact.
      if (!d.groups) for (const n of d.nudges || []) if (!map[n.contactId]) map[n.contactId] = { contactId: n.contactId, reasons: [n] };
      setFollowUps(map);
      setFollowState({ loading: false, error: null });
    } catch (err) {
      setFollowUps({});
      setFollowState({ loading: false, error: messageFromError(err, 'Follow-ups did not load.') });
    }
  }, []);

  useEffect(() => {
    api('/api/tasks').then((r) => r.json()).then((d) => setTasks(d.tasks || [])).catch(() => {});   // health score input only
    loadFollowUps();
  }, [loadFollowUps]);

  // Returns true when it loaded. quiet: keep what is on screen while it loads (after a write).
  const fetchBoardData = useCallback(async (boardId, { quiet = false } = {}) => {
    if (!boardId) return false;
    const seq = ++loadSeq.current;
    if (!quiet) setBoard({ ...EMPTY_BOARD, boardId, loading: true });
    try {
      const [contactsRes, detailRes] = await Promise.all([
        api(`/api/crm-boards/${boardId}/contacts`),
        api(`/api/crm-boards/${boardId}`),
      ]);
      const [cd, dd] = await Promise.all([contactsRes.json(), detailRes.json()]);
      if (seq !== loadSeq.current) return false;
      setBoard({ boardId, loading: false, error: null, lanes: lanesFrom(cd), columns: dd.columns || [] });
      return true;
    } catch (err) {
      if (seq !== loadSeq.current) return false;
      if (!quiet) setBoard({ ...EMPTY_BOARD, boardId, loading: false, error: messageFromError(err, 'This board did not load.') });
      return false;
    }
  }, []);

  const loadBoards = useCallback(async ({ pick } = {}) => {
    setBoardsState({ loading: true, error: null });
    try {
      let d = await (await api('/api/crm-boards')).json();
      let list = d.boards || [];
      const canManage = d.permissions?.managePipeline === true;
      if (list.length === 0 && canManage) {
        // The first visit after the move to boards: the old single pipeline becomes a board.
        const mig = await (await api('/api/crm-boards/migrate-legacy', { method: 'POST' })).json();
        if (!mig.skipped && mig.contactsMigrated) showSuccessToast(`Moved ${mig.contactsMigrated} contact${mig.contactsMigrated !== 1 ? 's' : ''} onto your new CRM board.`);
        d = await (await api('/api/crm-boards')).json();
        list = d.boards || [];
      }
      setBoards(list);
      setManagePipeline(canManage);
      setActiveBoardId((cur) => {
        const want = pick || cur;
        if (want && list.some((b) => b.id === want)) return want;
        return (list.find((b) => b.isDefault) || list[0])?.id ?? null;
      });
      setBoardsState({ loading: false, error: null });
      return list;
    } catch (err) {
      setBoardsState({ loading: false, error: messageFromError(err, 'Your boards did not load.') });
      return null;
    }
  }, []);

  useEffect(() => { loadBoards(); }, [loadBoards]);
  useEffect(() => {
    if (activeBoardId) fetchBoardData(activeBoardId);
    else setBoard({ ...EMPTY_BOARD, loading: false });
  }, [activeBoardId, fetchBoardData]);

  // ── Delayed deletes (decision 7) ─────────────────────────────────────────
  const activeBoardRef = useRef(null);
  activeBoardRef.current = activeBoardId;
  const cardRemoval = useDelayedDelete({
    send: (item, { keepalive }) => api(`/api/crm-boards/${item.boardId}/contacts/${item.contact.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (item) => {
      if (item.boardId !== activeBoardRef.current) return;
      setBoard((b) => ({ ...b, lanes: b.lanes.map((s) => ({ ...s, contacts: s.contacts.filter((c) => c.id !== item.contact.id) })) }));
      setBoards((list) => list.map((x) => (x.id === item.boardId ? { ...x, cardCount: Math.max(0, (x.cardCount || 1) - 1) } : x)));
    },
    describe: (item) => `${item.contact.name} removed from the board.`,
    failMessage: 'That could not be removed from the board.',
  });
  const removeCard = (contact) => {
    if (selectedId === contact.id) setSelectedId(null);
    cardRemoval.remove({ id: `${activeBoardId}:${contact.id}`, boardId: activeBoardId, contact });
  };

  const boardRemoval = useDelayedDelete({
    send: (b, { keepalive }) => api(`/api/crm-boards/${b.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (b) => setBoards((list) => list.filter((x) => x.id !== b.id)),
    describe: (b) => `${b.name} deleted.`,
    failMessage: 'The board could not be deleted.',
  });

  // The old JSON interactions on a contact: each deletes with Undo, then one PUT of what is left.
  const legacyRemoval = useDelayedDelete({
    send: (item, { keepalive }) => {
      const card = lanesRef.current.flatMap((s) => s.contacts).find((c) => c.id === item.contactId);
      const rest = (card?.interactions || []).filter((i) => String(i.id) !== String(item.interactionId));
      return api(`/api/contacts/${item.contactId}`, { method: 'PUT', body: JSON.stringify({ interactions: rest }), keepalive });
    },
    onDeleted: (item) => {
      const drop = (c) => (c.id !== item.contactId ? c : { ...c, interactions: (c.interactions || []).filter((i) => String(i.id) !== String(item.interactionId)) });
      setBoard((b) => ({ ...b, lanes: b.lanes.map((s) => ({ ...s, contacts: s.contacts.map(drop) })) }));
      setContacts((prev) => prev.map(drop));
    },
    describe: () => 'Interaction deleted.',
    failMessage: 'The interaction could not be deleted.',
  });

  // ── What is on screen ─────────────────────────────────────────────────────
  const visibleBoards = boards.filter((b) => !boardRemoval.isHidden(b.id));
  useEffect(() => {
    // The active board was just deleted (or is waiting out its Undo): show another one.
    if (activeBoardId && boardRemoval.isHidden(activeBoardId)) {
      const next = boards.find((b) => !boardRemoval.isHidden(b.id));
      setActiveBoardId(next?.id ?? null);
    }
  }, [activeBoardId, boards, boardRemoval]);
  const activeBoard = boards.find((b) => b.id === activeBoardId) || null;
  const currency = boardCurrency(board.columns);

  // Lanes as the board stands: removed cards hidden; a client signed over 60 days ago drops off.
  const lanes = useMemo(() => board.lanes.map((s) => ({
    ...s,
    contacts: s.contacts.filter((c) => !cardRemoval.isHidden(`${activeBoardId}:${c.id}`) && !isSignedLongAgo(c)),
  })).filter((s) => s.id !== UNASSIGNED_ID || s.contacts.length > 0), [board.lanes, cardRemoval, activeBoardId]);
  const realStages = lanes.filter((s) => s.id !== UNASSIGNED_ID);
  const hasStages = board.lanes.some((s) => s.id !== UNASSIGNED_ID);

  const colorOf = useCallback((stage) => {
    if (!stage || stage.id === UNASSIGNED_ID) return 'pastel-neutral';
    const i = realStages.findIndex((s) => s.id === stage.id);
    return stageColor(stage, i < 0 ? 0 : i);
  }, [realStages]);

  // Decision 80: a card in a lost stage is not chased, so it carries no follow-up on this board
  // (flag, filter, count and drawer all read this one map).
  const followUps = useMemo(() => {
    const m = { ...allFollowUps };
    for (const s of lanes) if (s.id !== UNASSIGNED_ID && isLostStage(s)) for (const c of s.contacts) delete m[c.id];
    return m;
  }, [allFollowUps, lanes]);
  const stats = useMemo(() => pipelineStats(lanes, followUps), [lanes, followUps]);

  const q = search.trim().toLowerCase();
  const shownLanes = useMemo(() => lanes.map((s) => ({
    ...s,
    contacts: s.contacts.filter((c) => {
      if (followOnly && !followUps[c.id]) return false;
      if (!q) return true;
      return [c.name, c.email, c.company].some((x) => String(x || '').toLowerCase().includes(q));
    }),
  })), [lanes, followOnly, followUps, q]);
  const shownCount = shownLanes.reduce((n, s) => n + s.contacts.length, 0);

  const selected = useMemo(() => lanes.flatMap((s) => s.contacts).find((c) => c.id === selectedId) || null, [lanes, selectedId]);
  const selectedStage = selected ? stageOfCard(lanes, selected.id) : null;

  // ── Moves (drag, touch drag, Move to stage…, the drawer's pills) ─────────
  async function moveContact(contactId, toStageId, { offerUndo = true } = {}) {
    const boardId = activeBoardId;
    const from = stageOfCard(lanesRef.current, contactId);
    const card = from?.contacts.find((c) => c.id === contactId);
    const to = lanesRef.current.find((s) => s.id === toStageId);
    if (!boardId || !from || !card || !to || from.id === toStageId || toStageId === UNASSIGNED_ID) return;
    setBoard((b) => ({ ...b, lanes: moveIn(b.lanes, contactId, toStageId) }));
    try {
      await api(`/api/crm-boards/${boardId}/contacts/${contactId}/stage`, { method: 'PATCH', body: JSON.stringify({ stageId: toStageId }) });
    } catch (err) {
      showErrorToast(`${card.name} was not moved. ${messageFromError(err, 'Please try again.')}`);
      // Keep the card where the server has it: read the board again; if that fails too, put it back.
      const ok = await fetchBoardData(boardId, { quiet: true });
      if (!ok && activeBoardRef.current === boardId) setBoard((b) => ({ ...b, lanes: moveIn(b.lanes, contactId, from.id) }));
      return;
    }
    const canUndo = offerUndo && from.id !== UNASSIGNED_ID;
    showToast(`${card.name} moved to ${to.name}.`, {
      type: 'success',
      ...(canUndo ? { action: { label: 'Undo', onClick: () => { if (activeBoardRef.current === boardId) moveContact(contactId, from.id, { offerUndo: false }); } } } : {}),
    });
  }

  function onDragStart(e, contactId) {
    dragRef.current = contactId;
    setDraggingId(contactId);
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(contactId)); } catch { /* old browsers */ }
  }
  function onDragEnd() { dragRef.current = null; setDraggingId(null); }
  function onDrop(stageId) {
    const id = dragRef.current;
    onDragEnd();
    if (id) moveContact(id, stageId);
  }
  function onTouchDragStart(contactId) { dragRef.current = contactId; setDraggingId(contactId); }
  function onTouchDrop(stageId) {
    const id = dragRef.current;
    onDragEnd();
    if (id && stageId) moveContact(id, stageId);
  }

  // ── Card fields (deal value, next step, the board's columns) ─────────────
  async function saveField(contactId, key, value) {
    const boardId = activeBoardId;
    const card = lanesRef.current.flatMap((s) => s.contacts).find((c) => c.id === contactId);
    if (!card) return;
    const before = card.boardEntry?.customFields?.[key];
    const put = (val) => setBoard((b) => ({
      ...b,
      lanes: b.lanes.map((s) => ({ ...s, contacts: s.contacts.map((c) => (c.id !== contactId ? c : { ...c, boardEntry: { ...(c.boardEntry || {}), customFields: { ...(c.boardEntry?.customFields || {}), [key]: val } } })) })),
    }));
    put(value);
    try {
      await api(`/api/crm-boards/${boardId}/contacts/${contactId}/fields`, { method: 'PATCH', body: JSON.stringify({ customFields: { [key]: value } }) });
    } catch (err) {
      if (activeBoardRef.current === boardId) put(before);
      showErrorToast(`That change was not saved. ${messageFromError(err, 'Please try again.')}`);
    }
  }

  // ── Adding contacts ───────────────────────────────────────────────────────
  // Creates the contact (POST /api/contacts, the Contacts route, used as it is), then the card at
  // the stage, then the value. A failure after the contact exists says so, so nobody adds it twice.
  async function addContact({ value, stageId, existingId, ...contactData }) {
    const boardId = activeBoardId;
    let contact = existingId ? contacts.find((c) => c.id === existingId) : null;
    if (!contact) {
      const res = await api('/api/contacts', { method: 'POST', body: JSON.stringify(contactData) });
      contact = (await res.json()).contact;
      setContacts((prev) => [contact, ...prev]);
    }
    try {
      await api(`/api/crm-boards/${boardId}/contacts`, { method: 'POST', body: JSON.stringify({ contactId: contact.id, stageId: stageId || undefined }) });
      if (value !== null && value !== undefined) {
        await api(`/api/crm-boards/${boardId}/contacts/${contact.id}/fields`, { method: 'PATCH', body: JSON.stringify({ customFields: { deal_value: value } }) });
      }
      setBoards((list) => list.map((b) => (b.id === boardId ? { ...b, cardCount: (b.cardCount || 0) + 1 } : b)));
      showSuccessToast(`${contact.name} added.`);
    } catch (err) {
      showErrorToast(`${contact.name} is in Contacts but was not added to the board. ${messageFromError(err, '')}`.trim());
    }
    await fetchBoardData(boardId, { quiet: true });
    return contact;
  }
  async function saveFromModal(form) {
    const contact = await addContact(form);
    setAddOpen(false);
    if (contact) setSelectedId(contact.id);
  }
  async function quickAdd(name, stageId) {
    try {
      await addContact({ name, source: 'manual', tags: [], stageId, value: null });
      return true;
    } catch (err) {
      showErrorToast(`${name} was not added. ${messageFromError(err, 'Please try again.')}`);
      return false;
    }
  }

  // ── Boards ────────────────────────────────────────────────────────────────
  async function createBoard({ name, description, boardType, workflowDraft }) {
    const body = { name, description, boardType };
    if (workflowDraft) {
      body.stages = (workflowDraft.stages || []).map(({ name: n, colour, position, isTerminal, isStart }) => ({ name: n, colour, position, isTerminal, isStart }));
      body.columns = (workflowDraft.columns || []).map(({ name: n, fieldKey, fieldType, options, position, isRequired }) => ({ name: n, fieldKey, fieldType, options, position, isRequired }));
    }
    // A failure throws to the modal, which shows the server's words and stays open; the modal
    // says "created" once this has returned.
    const res = await api('/api/crm-boards', { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json();
    await loadBoards({ pick: d.board.id });
    setCreateOpen(false);
    setSelectedId(null);
  }
  async function afterSettings({ close }) {
    await loadBoards();
    if (activeBoardId) await fetchBoardData(activeBoardId, { quiet: true });
    if (close) setSettingsOpen(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const subline = activeBoard && !board.loading && !board.error
    ? `${stats.total} contact${stats.total !== 1 ? 's' : ''}${stats.valuedOpen > 0 ? ` · ${money(stats.openValue, currency)} open pipeline` : ''}`
    : undefined;

  const wonStages = realStages.filter(isWonStage);
  const kpis = [];
  if (stats.valuedOpen > 0) kpis.push({ label: 'Open pipeline', value: money(stats.openValue, currency), hint: `${stats.openCount} open deal${stats.openCount !== 1 ? 's' : ''}`, color: 'pastel-periwinkle' });
  if (wonStages.length > 0) kpis.push({ label: 'Won', value: String(stats.wonCount), hint: `In ${wonStages.map((s) => s.name).join(', ')}`, color: 'pastel-mint' });
  if (stats.winRate !== null) kpis.push({ label: 'Win rate', value: `${stats.winRate}%`, hint: 'Won, of open and won deals', color: 'pastel-blue' });
  if (!followState.loading && !followState.error) kpis.push({ label: 'Needs follow-up', value: String(stats.followCount), hint: 'From your follow-ups', color: 'pastel-pink' });

  const header = (
    <PageHeader
      eyebrow={{ label: 'Business workspace', color: 'group-clients' }}
      help="crm"
      title="CRM pipeline"
      subline={subline}
      primary={<>
        <SearchInput className="crm-search" data-tour-id="crm-search" label="Search contacts or companies" placeholder="Search contacts or companies…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {activeBoard && hasStages && (
          <HeaderButton primary data-tour-id="crm-add-contact" onClick={() => setAddOpen(true)}>+ Add contact</HeaderButton>
        )}
      </>}
    />
  );

  let body;
  if (boardsState.loading && boards.length === 0) {
    body = <Skeleton lines={4} label="Loading your pipeline" />;
  } else if (boardsState.error) {
    body = <ErrorState title="Your pipeline did not load" message={boardsState.error} onRetry={() => loadBoards()} />;
  } else if (visibleBoards.length === 0) {
    body = (
      <EmptyState icon="🧭" title="No pipeline yet"
        action={managePipeline ? <Button variant="create" onClick={() => setCreateOpen(true)}>+ New board</Button> : null}>
        {managePipeline ? 'Make a board to follow your clients from first enquiry to signed.' : 'Your business has no pipeline yet. The owner or an admin can create one.'}
      </EmptyState>
    );
  } else if (board.loading) {
    body = <Skeleton lines={6} label="Loading the board" />;
  } else if (board.error) {
    body = <ErrorState title={`${activeBoard?.name || 'This board'} did not load`} message={board.error} onRetry={() => fetchBoardData(activeBoardId)} />;
  } else if (!hasStages) {
    // 2026-10-08: a board with no stages showed only the set-up card, so a contact added to it (In
    // CRM on Contacts) was on the board and visible nowhere. Its cards are in the Unassigned lane;
    // the lane is shown under the set-up card whenever it holds anyone. Picking stages moves them
    // to the first stage (apply-template).
    const waiting = lanes.filter((s) => s.unassigned && s.contacts.length > 0);
    body = (
      <>
        <BoardSetup board={activeBoard} canManage={managePipeline} onApplied={async () => { await fetchBoardData(activeBoardId); showSuccessToast('Stages added.'); }} />
        {waiting.length > 0 && (
          <div className="crm-board crm-board--waiting" role="region" aria-label={`${activeBoard?.name} contacts without a stage`} data-testid="crm-unassigned-waiting">
            <div className="crm-board__track">
              {waiting.map((stage) => (
                <KanbanColumn key={stage.id} stage={stage} color={colorOf(stage)} cardColor={colorOf(stage)} currency={currency}
                  selectedId={selectedId} followUps={followUps} draggingId={draggingId}
                  onOpen={setSelectedId} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDrop}
                  onTouchDragStart={onTouchDragStart} onTouchDrop={onTouchDrop}
                  onMoveTo={setMoveFor} onRemove={removeCard} />
              ))}
            </div>
          </div>
        )}
      </>
    );
  } else {
    body = (
      <>
        <section className="crm-kpis" data-tour-id="crm-pipeline-stats" aria-label="Pipeline numbers">
          {kpis.map((k) => <StatTile key={k.label} card label={k.label} value={k.value} hint={k.hint} color={k.color} />)}
        </section>
        {followState.error && (
          <Banner tone="problem" action={<Button variant="secondary" size="sm" onClick={loadFollowUps}>Try again</Button>}>
            Follow-ups did not load: {followState.error}
          </Banner>
        )}
        <div data-tour-id="crm-kanban-board">
          {(q || followOnly) && shownCount === 0 ? (
            <div className="crm-search-empty">{q ? `No contacts match “${search.trim()}”.` : 'No one on this board needs a follow-up.'}</div>
          ) : view === 'list' ? (
            <PipelineList lanes={shownLanes} colorOf={colorOf} currency={currency} followUps={followUps} onOpen={setSelectedId} />
          ) : (
            <div className="crm-board" role="region" aria-label={`${activeBoard?.name} board`} tabIndex={-1}>
              <div className="crm-board__track">
                {shownLanes.map((stage) => (
                  <KanbanColumn key={stage.id} stage={stage} color={colorOf(stage)} cardColor={colorOf(stage)} currency={currency}
                    selectedId={selectedId} followUps={followUps} draggingId={draggingId}
                    onOpen={setSelectedId} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDrop}
                    onTouchDragStart={onTouchDragStart} onTouchDrop={onTouchDrop}
                    onMoveTo={setMoveFor} onRemove={removeCard} onQuickAdd={quickAdd} />
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <PageLayout data-page="crm" data-testid="crm-page">
      {header}

      {visibleBoards.length > 0 && !boardsState.error && (
        <div className="crm-bar">
          <div className="crm-tabs" role="tablist" aria-label="Boards" data-tour-id="crm-board-selector">
            {visibleBoards.map((b) => {
              const on = b.id === activeBoardId;
              const count = on && !board.loading && !board.error ? stats.total : (b.cardCount ?? 0);
              return (
                <button key={b.id} type="button" role="tab" aria-selected={on} className="crm-tab"
                  style={{ '--ui-bg': v(boardColor(boards.findIndex((x) => x.id === b.id))) }}
                  onClick={() => { if (!on) { setActiveBoardId(b.id); setSelectedId(null); setFollowOnly(false); } }}>
                  {b.name}<span className="crm-tab__count" aria-label={`${count} contacts`}>{count}</span>
                </button>
              );
            })}
            {managePipeline && <button type="button" className="crm-tab crm-tab--new" onClick={() => setCreateOpen(true)}>+ New board</button>}
          </div>
          <div className="crm-controls">
            {hasStages && !board.loading && !board.error && (
              <>
                <SegmentedControl label="View" value={view} onChange={setView} options={[{ value: 'board', label: 'Board' }, { value: 'list', label: 'List' }]} />
                {!followState.error && (
                  <Chip className="crm-follow" selected={followOnly} disabled={followState.loading} onClick={() => setFollowOnly((f) => !f)}>
                    Needs follow-up · {followState.loading ? '…' : stats.followCount}
                  </Chip>
                )}
              </>
            )}
            {managePipeline && activeBoard && <Button variant="secondary" size="sm" onClick={() => setSettingsOpen(true)}>Edit stages</Button>}
          </div>
        </div>
      )}
      {activeBoard?.description && hasStages && <p className="crm-muted">{activeBoard.description}</p>}

      {body}

      <CrmDrawer
        contact={selected}
        stage={selectedStage}
        stages={lanes}
        colorOf={colorOf}
        columns={board.columns}
        currency={currency}
        followUp={selected ? followUps[selected.id] : null}
        health={selected ? scores[selected.id] ?? null : null}
        legacyInteractions={selected ? (selected.interactions || []).filter((i) => i && i.id !== undefined && !legacyRemoval.isHidden(`${selected.id}:${i.id}`)) : []}
        onDeleteLegacy={(c, interactionId) => legacyRemoval.remove({ id: `${c.id}:${interactionId}`, contactId: c.id, interactionId })}
        onClose={() => setSelectedId(null)}
        onMove={(id, stageId) => moveContact(id, stageId)}
        onFieldSave={saveField}
        onRemove={removeCard}
        onRefreshHealth={recalculate}
        onContactUpdated={() => fetchBoardData(activeBoardId, { quiet: true })}
      />

      <MoveToSheet
        open={!!moveFor}
        onClose={() => setMoveFor(null)}
        itemName={moveFor?.name}
        current={moveFor ? stageOfCard(lanes, moveFor.id)?.id : undefined}
        destinations={realStages.map((s) => ({ id: s.id, label: s.name, icon: '', color: colorOf(s) }))}
        onMove={(d) => { const c = moveFor; setMoveFor(null); if (c) moveContact(c.id, d.id); }}
      />

      <ContactFormModal open={addOpen} stages={realStages} defaultStageId={(realStages.find((s) => s.isStart) || realStages[0])?.id}
        currencySign={currencySymbol(currency).trim()} onSave={saveFromModal} onClose={() => setAddOpen(false)}
        existing={addOpen ? contacts.filter((c) => !board.lanes.some((s) => s.contacts.some((x) => x.id === c.id))) : []} />
      <CreateBoardModal open={createOpen} onSave={createBoard} onClose={() => setCreateOpen(false)} />
      <BoardSettingsModal open={settingsOpen && !!activeBoard} board={activeBoard} canDelete={visibleBoards.length > 1}
        onClose={() => setSettingsOpen(false)} onSaved={afterSettings}
        onDelete={(b) => { setSelectedId(null); boardRemoval.remove(b); }} />
    </PageLayout>
  );
}
